<template>
  <b-modal
    id="add-to-design-gallery-modal"
    centered
    hide-header
    hide-footer
    body-class="p-0"
    content-class="dg-modal-content gallery-modal"
  >
    <div class="dg-modal">
      <!-- Header -->
      <div class="dg-header">
        <div class="dg-header-icon">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
            class="lucide lucide-image-plus h-5 w-5 text-primary"
          >
            <path d="M16 5h6" />
            <path d="M19 2v6" />
            <path d="M21 11.5V19a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h7.5" />
            <path d="m21 15-3.086-3.086a2 2 0 0 0-2.828 0L6 21" />
            <circle
              cx="9"
              cy="9"
              r="2"
            />
          </svg>
        </div>
        <div>
          <h5 class="dg-title">
            Add to Design Gallery
          </h5>
          <p class="dg-subtitle">
            Save this design with optimized presets for browsing and reuse.
          </p>
        </div>
        <button
          class="dg-close-btn"
          :disabled="isLoading"
          @click="closeModal"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          ><line
            x1="18"
            y1="6"
            x2="6"
            y2="18"
          /><line
            x1="6"
            y1="6"
            x2="18"
            y2="18"
          /></svg>
        </button>
      </div>

      <!-- Tabs -->
      <div class="dg-tabs">
        <button
          class="dg-tab"
          :class="{ 'dg-tab--active': activeTab === 0 }"
          @click="activeTab = 0"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
            class="lucide lucide-sparkles h-3.5 w-3.5"
          ><path d="M9.937 15.5A2 2 0 0 0 8.5 14.063l-6.135-1.582a.5.5 0 0 1 0-.962L8.5 9.936A2 2 0 0 0 9.937 8.5l1.582-6.135a.5.5 0 0 1 .963 0L14.063 8.5A2 2 0 0 0 15.5 9.937l6.135 1.581a.5.5 0 0 1 0 .964L15.5 14.063a2 2 0 0 0-1.437 1.437l-1.582 6.135a.5.5 0 0 1-.963 0z" /><path d="M20 3v4" /><path d="M22 5h-4" /><path d="M4 17v2" /><path d="M5 18H3" /></svg>
          Basic
        </button>
        <button
          class="dg-tab"
          :class="{ 'dg-tab--active': activeTab === 1 }"
          @click="activeTab = 1"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
            class="dg-tab-icon"
          ><path d="M20 7h-9" /><path d="M14 17H5" /><circle
            cx="17"
            cy="17"
            r="3"
          /><circle
            cx="7"
            cy="7"
            r="3"
          /></svg>
          Advanced
        </button>
      </div>

      <!-- Body -->
      <b-overlay
        :show="isLoading"
        variant="white"
      >
        <div class="dg-body">
          <!-- Basic Tab -->
          <div v-if="activeTab === 0">
            <!-- Name -->
            <div class="dg-field">
              <div class="dg-field-label-row">
                <label class="dg-label">Name</label>
                <button
                  class="dg-link-btn"
                  :disabled="isLoading"
                  @click="pasteProjectName"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    class="lucide lucide-clipboard-paste h-3 w-3"
                  ><path d="M15 2H9a1 1 0 0 0-1 1v2c0 .6.4 1 1 1h6c.6 0 1-.4 1-1V3c0-.6-.4-1-1-1Z" /><path d="M8 4H6a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2M16 4h2a2 2 0 0 1 2 2v2M11 14h10" /><path d="m17 10 4 4-4 4" /></svg>
                  Paste project name
                </button>
              </div>
              <input
                v-model="templateName"
                class="dg-input"
                placeholder="Enter gallery item name…"
              >
            </div>

            <!-- Type -->
            <div class="dg-field">
              <label class="dg-label">Type</label>
              <div class="dg-chips">
                <button
                  v-for="option in typeOptions"
                  :key="option.value"
                  class="dg-chip"
                  :class="{ 'dg-chip--active': selectedType === option.value }"
                  @click="selectedType = option.value"
                >
                  {{ option.label }}
                </button>
              </div>
            </div>

            <!-- Style -->
            <div class="dg-field">
              <label class="dg-label">Style</label>
              <div class="dg-chips">
                <button
                  v-for="option in styleOptions"
                  :key="option.value"
                  class="dg-chip"
                  :class="{ 'dg-chip--active': selectedStyle === option.value }"
                  @click="selectedStyle = option.value"
                >
                  {{ option.label }}
                </button>
              </div>
            </div>

            <!-- Presets note -->
            <div class="dg-preset-alert">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
                class="dg-preset-icon"
              ><circle
                cx="12"
                cy="12"
                r="10"
              /><line
                x1="12"
                y1="8"
                x2="12"
                y2="12"
              /><line
                x1="12"
                y1="16"
                x2="12.01"
                y2="16"
              /></svg>
              <span><strong>Gallery presets applied:</strong> PNG, 72 DPI, 2000×2000 max. Edit these in the Advanced tab if needed.</span>
            </div>
          </div>

          <!-- Advanced Tab -->
          <div v-if="activeTab === 1">
            <!-- Customer -->
            <div class="dg-field">
              <label class="dg-label">Customer</label>
              <select
                v-model="customerId"
                class="dg-input"
                :disabled="!customers.length"
              >
                <option
                  v-if="!customers.length"
                  :value="customerId"
                >
                  {{ selectedCustomerName }}
                </option>
                <option
                  v-for="customer in customers"
                  :key="customer.id"
                  :value="customer.id"
                >
                  {{ customer.name }}
                </option>
              </select>
            </div>

            <!-- Tags -->
            <div class="dg-field">
              <label class="dg-label">Tags</label>
              <small class="dg-hint">Type and Style (set on Basic) are also tags. Add more here.</small>

              <div class="dg-tag-chips">
                <span class="dg-tag-chip dg-tag-chip--base">
                  {{ selectedTypeLabel }}
                  <span class="dg-tag-chip-kind">type</span>
                </span>
                <span class="dg-tag-chip dg-tag-chip--base">
                  {{ selectedStyleLabel }}
                  <span class="dg-tag-chip-kind">style</span>
                </span>
                <span
                  v-for="tag in selectedExtraTags"
                  :key="tag"
                  class="dg-tag-chip"
                >
                  {{ tag }}
                  <button
                    type="button"
                    class="dg-tag-chip-remove"
                    @click="removeExtraTag(tag)"
                  >
                    <svg
                      data-v-81735b14=""
                      xmlns="http://www.w3.org/2000/svg"
                      width="12"
                      height="12"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="2"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    ><path
                      data-v-81735b14=""
                      d="M18 6 6 18"
                    /><path
                      data-v-81735b14=""
                      d="m6 6 12 12"
                    /></svg>
                  </button>
                </span>
              </div>

              <div class="dg-tag-input-row">
                <input
                  v-model="customTagInput"
                  class="dg-input dg-input--sm"
                  placeholder="Add a tag..."
                  @keyup.enter.prevent="addTagFromInput"
                >
                <button
                  class="dg-tag-add-btn"
                  type="button"
                  @click="addTagFromInput"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  ><path d="M5 12h14" /><path d="M12 5v14" /></svg>
                </button>
              </div>

              <div class="dg-tag-suggestions">
                <button
                  v-for="tag in availableTagOptions"
                  :key="`tag-opt-${tag}`"
                  type="button"
                  class="dg-tag-suggestion"
                  @click="addExtraTag(tag)"
                >
                  + {{ tag }}
                </button>
              </div>
            </div>

            <!-- File settings -->
            <div class="dg-row">
              <div class="dg-field dg-field--third">
                <label class="dg-label">File Type</label>
                <select
                  v-model="fileType"
                  class="dg-input"
                >
                  <option
                    v-for="option in fileTypeOptions"
                    :key="option.value"
                    :value="option.value"
                  >
                    {{ option.text }}
                  </option>
                </select>
              </div>
              <div class="dg-field dg-field--third">
                <label class="dg-label">DPI</label>
                <input
                  v-model.number="dpi"
                  class="dg-input"
                  type="number"
                  min="1"
                >
              </div>
              <div class="dg-field dg-field--third">
                <label class="dg-label">Max Size (KB)</label>
                <input
                  v-model.number="maxFileSize"
                  class="dg-input"
                  type="number"
                  min="1"
                >
              </div>
            </div>

            <!-- Update Image File -->
            <div class="dg-field">
              <label class="dg-label">Update Image File</label>
              <div class="dg-row dg-row--align-end">
                <div class="dg-field dg-field--third">
                  <label
                    class="dg-label dg-label--sm"
                    for="gallery-thumbnail-width"
                  >Max Width</label>
                  <input
                    id="gallery-thumbnail-width"
                    v-model.number="thumbnailWidth"
                    class="dg-input"
                    type="number"
                    min="1"
                  >
                </div>
                <div class="dg-field dg-field--third">
                  <label
                    class="dg-label dg-label--sm"
                    for="gallery-thumbnail-height"
                  >Max Height</label>
                  <input
                    id="gallery-thumbnail-height"
                    v-model.number="thumbnailHeight"
                    class="dg-input"
                    type="number"
                    min="1"
                  >
                </div>
                <div class="dg-field dg-field--third">
                  <button
                    class="dg-btn dg-btn--outline dg-btn--block dg-btn--field-size"
                    @click="generateTemplateThumbnail"
                  >
                    Generate new
                  </button>
                </div>
              </div>

              <div class="dg-upload-link-row">
                <button
                  type="button"
                  class="dg-upload-link"
                  @click="showImageTools({ upload: true })"
                >
                  or upload image...
                </button>
              </div>

              <b-form-file
                v-if="showUploadSection"
                v-model="image"
                accept="image/*"
                class="mt-3"
                @input="handleImageChange"
              />

              <PreviewableImage
                v-if="isImageToolsVisible && templateImagePreview"
                :src="templateImagePreview"
                alt="template-thumbnail"
                class="mt-3"
              />
            </div>

            <!-- Update existing -->
            <div class="dg-checkbox-row">
              <input
                id="dg-update-existing"
                v-model="isUpdateExistingTemplate"
                type="checkbox"
                class="dg-checkbox"
              >
              <label
                for="dg-update-existing"
                class="dg-checkbox-label"
              >Update existing gallery item</label>
            </div>
          </div>
        </div>
      </b-overlay>

      <!-- Footer -->
      <div class="dg-footer">
        <small
          v-if="nextTemplateId"
          class="dg-gallery-id"
        >Gallery ID: {{ nextTemplateId }}</small>
        <div class="dg-footer-actions">
          <button
            class="dg-btn dg-btn--ghost"
            :disabled="isLoading"
            @click="closeModal"
          >
            Cancel
          </button>
          <button
            class="dg-btn dg-btn--primary"
            :disabled="isSaveDisabled || isLoading"
            @click="handleSave"
          >
            <svg
              v-if="!isLoading"
              xmlns="http://www.w3.org/2000/svg"
              width="15"
              height="15"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            ><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="17 8 12 3 7 8" /><line
              x1="12"
              y1="3"
              x2="12"
              y2="15"
            /></svg>
            <span>{{ isLoading ? 'Adding…' : 'Add to Gallery' }}</span>
          </button>
        </div>
      </div>
    </div>
  </b-modal>
</template>

<script>
import axios from 'axios';
import PreviewableImage from '@frontend/components/shared/PreviewableImage.vue';
import { GeneratePreviewImage } from '@frontend/group/generate-preview-image';
import { DESIRED_HEIGHT, DESIRED_WIDTH } from '@backend/template/generate-template-thumbnail';
import designGalleryModalSharedMixin from './design-gallery-modal-shared.mixin';
import {
  EVENTS,
  addToDesignGalleryEventBroker,
} from '@frontend/group/modules/add-to-design-gallery/event-broker';

export default {
  name: 'AddToDesignGalleryModal',
  components: {
    PreviewableImage,
  },
  mixins: [designGalleryModalSharedMixin],

  props: {
    customers: {
      type: Array,
      default: () => [],
    },
  },

  data() {
    return {
      template: null,
      activeTab: 0,
      templateName: '',
      projectName: '',
      selectedType: 'type_pdp',
      selectedStyle: 'style_clean',
      selectedExtraTags: [],
      customTagInput: '',
      tags: [],
      customerId: null,
      nextTemplateId: null,
      isUpdateExistingTemplate: false,
      isLoading: false,
      maxFileSize: 200,
      dpi: 72,
      fileType: 'png',
      fileTypeOptions: [
        { value: 'jpg', text: 'JPG' },
        { value: 'png', text: 'PNG' },
        { value: 'pdf', text: 'PDF' },
        { value: 'pptx', text: 'PPTX' },
        { value: 'jpg_from_psd', text: 'JPG from PSD' },
        { value: 'png_from_psd', text: 'PNG from PSD' },
      ],
      typeOptions: [
        { label: 'Flipbooks', value: 'type_flipbooks' },
        { label: 'PDP', value: 'type_pdp' },
        { label: 'Sell Sheet', value: 'type_sell_sheet' },
      ],
      styleOptions: [
        { label: 'Bold', value: 'style_bold' },
        { label: 'Clean', value: 'style_clean' },
        { label: 'Minimal', value: 'style_minimal' },
      ],
      image: null,
      previewObjectUrl: null,
      templateImagePreview: '',
      showUploadSection: false,
      isImageToolsVisible: false,
      thumbnailWidth: 2000,
      thumbnailHeight: 2000,
    };
  },

  computed: {
    selectedCustomerName() {
      const targetCustomerId = this.customerId ?? this.template?.customer_id;
      const selectedCustomer = this.customers.find(customer => `${customer.id}` === `${targetCustomerId}`);
      return selectedCustomer?.name || 'Current company';
    },

    finalTags() {
      return Array.from(new Set([
        'creative_studio',
        this.selectedType,
        this.selectedStyle,
        ...this.selectedExtraTags,
      ].filter(Boolean)));
    },

    isSaveDisabled() {
      return !this.templateName;
    },
  },

  mounted() {
    this.registerEventListeners();
  },

  methods: {
    registerEventListeners() {
      addToDesignGalleryEventBroker.on(EVENTS.INIT, this.initializeModal);
      addToDesignGalleryEventBroker.on(EVENTS.CLOSE, this.closeModal);
    },

    initializeModal({ preview }) {
      if (!preview?.template) {
        return;
      }

      this.resetForm();
      this.template = preview.template;
      this.template.preview = preview;

      this.templateName = `${this.template.name}_copy`;
      this.projectName = preview.data.project_name;
      this.customerId = this.template.customer_id || null;
      this.templateImagePreview = this.getTemplateImageUrl();
      this.fetchTags();
      this.fetchNextTemplateId();

      this.$bvModal.show('add-to-design-gallery-modal');
    },

    resetForm() {
      this.activeTab = 0;
      this.template = null;
      this.templateName = '';
      this.projectName = '';
      this.selectedType = 'type_pdp';
      this.selectedStyle = 'style_clean';
      this.selectedExtraTags = [];
      this.customTagInput = '';
      this.tags = [];
      this.customerId = null;
      this.nextTemplateId = null;
      this.isUpdateExistingTemplate = false;
      this.maxFileSize = 200;
      this.dpi = 72;
      this.fileType = 'png';
      this.image = null;
      this.isLoading = false;
      this.showUploadSection = false;
      this.isImageToolsVisible = false;
      this.thumbnailWidth = 2000;
      this.thumbnailHeight = 2000;

      this.clearPreviewObjectUrl();
      this.templateImagePreview = '';
    },

    pasteProjectName() {
      if (!this.projectName) {
        toastr.error('Project Name is empty');
        return;
      }
      this.templateName = this.projectName;
    },

    async fetchTags() {
      try {
        const { data } = await axios.get('/templates/tags');
        this.tags = data.map(tag => tag.name).filter(Boolean);
      } catch (error) {
        this.tags = [];
      }
    },
    
    async fetchNextTemplateId() {
      try {
        const { data } = await axios.get('/templates/next-template-id');
        this.nextTemplateId = data;
      } catch (error) {
        this.nextTemplateId = null;
      }
    },

    addTagFromInput() {
      this.addExtraTag(this.customTagInput);
    },

    closeModal() {
      this.$bvModal.hide('add-to-design-gallery-modal');
    },

    async handleSave() {
      this.isLoading = true;

      const shouldGenerateBackgroundThumbnail = !this.isUpdateExistingTemplate
        && !this.image
        && !this.templateImagePreview;
      const backgroundThumbnail = shouldGenerateBackgroundThumbnail
        ? await this.buildTemplateThumbnailFile()
        : null;

      const formData = new FormData();
      formData.append('templateName', this.templateName);
      formData.append('tags', this.finalTags.join(','));
      formData.append('isUpdateExistingTemplate', this.isUpdateExistingTemplate);
      formData.append('settings[max_file_size]', parseInt(this.maxFileSize, 10) || 200);
      formData.append('settings[dpi]', parseInt(this.dpi, 10) || 72);
      formData.append('settings[file_type]', this.fileType || 'png');
      formData.append('customerId', this.customerId ?? '');

      const imageToUpload = this.image || backgroundThumbnail;
      if (imageToUpload) {
        formData.append('image', imageToUpload);
      }

      addToDesignGalleryEventBroker.fire(EVENTS.SAVE, formData);
    },

    async buildTemplateThumbnailFile() {
      const canvas = this.template?.preview?.canvas || this.template?.canvas;
      if (!canvas) {
        return null;
      }

      const htmlCanvas = canvas.toCanvasElement ? canvas.toCanvasElement() : canvas;
      const width = this.thumbnailWidth || DESIRED_WIDTH;
      const height = this.thumbnailHeight || DESIRED_HEIGHT;
      const thumbnail = new GeneratePreviewImage(htmlCanvas).resize(width, height, true);
      return thumbnail.file('gallery-template-thumbnail');
    },

    setImagePreview(file) {
      this.image = file;
      this.previewObjectUrl = URL.createObjectURL(file);
      this.templateImagePreview = this.previewObjectUrl;
    },

    async generateTemplateThumbnail() {
      const thumbnailFile = await this.buildTemplateThumbnailFile();
      if (!thumbnailFile) {
        return;
      }

      this.clearPreviewObjectUrl();
      this.showImageTools();
      this.setImagePreview(await thumbnailFile);
    },

  },
};
</script>

<style scoped lang="scss">
@import "@/../sass/frontend/modal/design-modal";

:deep(.gallery-modal .dg-modal) {
  border-radius: 15px;
}
</style>

